import { useState, useEffect } from 'react';
import BusinessProfileForm from '../components/BusinessProfileForm';
import { useNavigate, useParams } from 'react-router-dom';
import { Stack } from '@mui/material';
import { useMutateBusinessProfileHandler } from '../dataHandlers/mutateBusinessProfileHandler';
import { useGetBusinessProfileHandler } from '../dataHandlers/getBusinessProfileHandler';
import { isBusinessProfileComplete } from '../../../constants/businessProfileStatusConstants.jsx';
import IncompleteBusinessProfileLeaveDialog from '../components/IncompleteBusinessProfileLeaveDialog';
import { usePermissions } from '../../../hooks/usePermissions';
import { UPDATE_BUSINESS_PROFILE } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function EditBusinessProfilePage() {
  const navigate = useNavigate();
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

  const { profile_id } = useParams();
  const onBusinessProfileCreated = () => {
    navigate('/business-profiles');
  };

  const { businessProfile, isLoading: isLoadingData } = useGetBusinessProfileHandler(profile_id);
  const { onSubmit, error, isPending } = useMutateBusinessProfileHandler({ onSuccess: onBusinessProfileCreated });

  const isIncomplete = Boolean(businessProfile && !isBusinessProfileComplete(businessProfile));
  const blockBrowserBack = isIncomplete && !isLoadingData;

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!hasEInvoicePermission(UPDATE_BUSINESS_PROFILE)) {
      showActionDeniedSnackbar();
      navigate('/business-profiles', { replace: true });
    }
  }, [loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  useEffect(() => {
    if (!blockBrowserBack) {
      return undefined;
    }

    window.history.pushState({ blockBrowserBack: true }, '');

    const handlePopState = () => {
      window.history.pushState({ blockBrowserBack: true }, '');
      setLeaveDialogOpen(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [blockBrowserBack]);

  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  const handleSubmit = async (payload) => {
    setIsSubmitting(true);
    onSubmit(payload, true);
  };

  function handleCancel() {
    if (isIncomplete) {
      setLeaveDialogOpen(true);
      return;
    }
    navigate('/business-profiles');
  }

  function handleLeaveIncomplete() {
    setLeaveDialogOpen(false);
    navigate('/business-profiles');
  }

  function handleCompleteSetup() {
    setLeaveDialogOpen(false);
  }

  if (loading || !hasEInvoicePermission(UPDATE_BUSINESS_PROFILE)) {
    return null;
  }

  if (isLoadingData || !businessProfile) {
    return null;
  }

  return (
    <Stack direction="column" sx={{ mt: '-2rem' }}>
      <BusinessProfileForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        initialData={businessProfile}
        edit={true}
        apiErrors={error}
        isLoading={isPending || isLoadingData || isSubmitting}
      />
      <IncompleteBusinessProfileLeaveDialog
        open={leaveDialogOpen}
        onClose={handleCompleteSetup}
        onLeave={handleLeaveIncomplete}
        onCompleteSetup={handleCompleteSetup}
      />
    </Stack>
  );
}
